import React, { type Ref } from 'react';

import Button from '@atlaskit/button/default/button';
import Code from '@atlaskit/code/code';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { usePanelManager } from '@atlassian/panel-system';
import { PanelProvider } from '@atlassian/panel-system/panel-provider';
import { PanelRouter } from '@atlassian/panel-system/panel-router';
import { PanelsRenderer } from '@atlassian/panel-system/panels-renderer';
import { Trigger } from '@atlassian/panel-system/trigger';

import { panelEntryPoint } from '../utils/dummy-entrypoint';
import DummyEntryPointComponent from '../utils/dummy-entrypoint-component';
import { RelayEnvironmentWrapper } from '../utils/relay-environment-wrapper';

const styles = cssMap({
	layout: {
		height: '100vh',
		width: '100%',
		overflow: 'hidden',
	},
	mainContent: {
		flex: 1,
		minWidth: '0px',
		paddingBlockStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		backgroundColor: token('color.background.neutral.subtle'),
		overflow: 'auto',
	},
	panelWrapper: {
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		flexShrink: 0,
		overflow: 'hidden',
		height: '100vh',
		display: 'flex',
		flexDirection: 'column',
		width: '400px',
	},
});

function LoadingMethodsExample() {
	const { manager } = usePanelManager();

	const onClickWithoutEntrypoint = () => {
		manager.openPanel({
			instanceId: 'panelWithoutEntryPoint',
			panelContent: <DummyEntryPointComponent />,
		});
	};

	return (
		<Flex xcss={styles.layout}>
			<Box xcss={styles.mainContent}>
				<Stack space="space.300">
					<Heading size="medium">All loading methods</Heading>
					<Text>This example demonstrates all ways to load a panel</Text>

					<Stack space="space.200">
						<Heading size="small" as="h3">
							Open Panel
						</Heading>
						<Text color="color.text.subtle" size="small">
							Click to open a panel. The panel loads the entry point.
						</Text>
						<Stack space="space.200">
							<Inline space="space.100" alignBlock="baseline">
								<Text>Open panel via Trigger</Text>
								<Trigger panel={panelEntryPoint} params={{}}>
									{({ ref }) => (
										<Button ref={ref as Ref<HTMLButtonElement>} testId="open-panel-1">
											Open panel
										</Button>
									)}
								</Trigger>
							</Inline>
							<Inline space="space.100" alignBlock="baseline">
								<Text>
									Open panel via <Code>manager.openPanel()</Code>
								</Text>
								<Button onClick={onClickWithoutEntrypoint} testId="open-panel-3">
									Open panel
								</Button>
							</Inline>
						</Stack>
					</Stack>
				</Stack>
			</Box>

			<PanelsRenderer>
				<PanelRouter
					wrapper={({ children }: { children: React.ReactNode }) => (
						<Box xcss={styles.panelWrapper}>{children}</Box>
					)}
					errorFallback={() => <Text as="p">Error loading panel</Text>}
				/>
			</PanelsRenderer>
		</Flex>
	);
}

export default function LoadingMethodsExampleWithProvider(): React.JSX.Element {
	return (
		<PanelProvider>
			<RelayEnvironmentWrapper>
				<LoadingMethodsExample />
			</RelayEnvironmentWrapper>
		</PanelProvider>
	);
}
